<template>
  <Teleport to="body">
    <div v-show="isVisible" class="mention-autocomplete" :style="dropdownStyle">
      <div
        v-for="(member, index) in membersList"
        :key="member.id"
        :class="['mention-item', { active: index === selectedIdx }]"
        @click="$emit('select', member)"
        @mouseenter="$emit('hover', index)"
      >
        <div class="mention-avatar">👤</div>
        <div class="mention-info">
          <div class="mention-username">@{{ member.username }}</div>
          <div v-if="member.name" class="mention-name">{{ member.name }}</div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  show: { type: Boolean, default: false },
  position: { type: Object, default: () => ({ x: 0, y: 0 }) },
  filteredMembers: { type: Array, default: () => [] },
  selectedIndex: { type: Number, default: 0 },
})

defineEmits(['select', 'hover'])

// Ensure we have proper unwrapped values
const isVisible = computed(() => {
  const showValue =
    typeof props.show === 'object' && props.show?.value !== undefined
      ? props.show.value
      : props.show
  const members = Array.isArray(props.filteredMembers)
    ? props.filteredMembers
    : props.filteredMembers?.value || []
  return showValue && members.length > 0
})

const membersList = computed(() => {
  const members = Array.isArray(props.filteredMembers)
    ? props.filteredMembers
    : props.filteredMembers?.value || []
  return members.filter(m => m && m.id && m.username)
})

const selectedIdx = computed(() => {
  return typeof props.selectedIndex === 'object' && props.selectedIndex?.value !== undefined
    ? props.selectedIndex.value
    : props.selectedIndex
})

const positionValue = computed(() => {
  if (typeof props.position === 'object' && props.position?.value) {
    return props.position.value
  }
  return props.position || { x: 0, y: 0 }
})

// Compute style based on whether we're positioning above or below
const dropdownStyle = computed(() => {
  const pos = positionValue.value
  if (pos.positionAbove) {
    // Position above: anchor bottom edge to the y coordinate
    return {
      left: `${pos.x}px`,
      bottom: `${window.innerHeight - pos.y}px`,
      top: 'auto',
    }
  } else {
    // Position below: anchor top edge to the y coordinate
    return {
      left: `${pos.x}px`,
      top: `${pos.y}px`,
      bottom: 'auto',
    }
  }
})
</script>

<style scoped>
.mention-autocomplete {
  position: fixed;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 4px 0;
  min-width: 200px;
  max-width: 300px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 10001;
  font-size: 14px;
}

.mention-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  gap: 10px;
  transition: background 0.15s;
}

.mention-item:hover,
.mention-item.active {
  background: #f0f0f0;
}

.mention-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.mention-info {
  flex: 1;
  min-width: 0;
}

.mention-username {
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mention-name {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
